import React, { useState, useEffect } from "react";
import axios from "axios";
import { FaCheck, FaTimes, FaMapMarkerAlt, FaGlobe, FaSearch, FaHistory, FaInfoCircle, FaPhoneAlt, FaEnvelope, FaCalendarAlt, FaDownload, FaEye, FaUsers, FaBullhorn, FaFilter } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { motion, AnimatePresence } from "framer-motion";
import InfluenerRaiseVoiceTabs from "./InfluenerRaiseVoiceTabs";

const InfluenerGetNewRaiseVoice = () => {
  const navigate = useNavigate();
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAd, setselectedAd] = useState(null);
  const [modal, setModal] = useState({ open: false, action: null, adId: null });
  const [rejectReason, setRejectReason] = useState("");
  const [refetchTrigger, setRefetchTrigger] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showApproveSpinner, setShowApproveSpinner] = useState(false);
  const [showRejectSpinner, setShowRejectSpinner] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  useEffect(() => {
    const fetchVoices = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/influencer/get/new/voice`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (response.data.data) {
          setVoices(response.data.data);
        } else {
          setVoices([]);
        }
      } catch (err) {
        setError("Failed to fetch voices");
      } finally {
        setLoading(false);
      }
    };
    fetchVoices();
  }, [refetchTrigger]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleApprove = async (id) => {
    setShowApproveSpinner(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/influencer/accept/ryv/ad/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.data && response.data.success) {
        toast.success("Voice accepted successfully");
        setTimeout(() => {
          setShowApproveSpinner(false);
          setRefetchTrigger(!refetchTrigger);
        }, 1500);
      } else {
        setShowApproveSpinner(false);
        toast.error(response.data.message || "Failed to accept voice");
      }
    } catch (error) {
      setShowApproveSpinner(false);
      toast.error(error.response?.data?.message || "Failed to accept voice");
    }
  };

  const handleReject = async (id, reason) => {
    if (!reason || reason.trim() === "") {
      toast.error("Please provide a reason for rejection");
      return;
    }
    setShowRejectSpinner(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/influencer/reject/ryv/ad/${id}`,
        { rejectionNote: reason.trim() },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.data && response.data.success) {
        toast.success(response.data.message || "Voice rejected successfully");
        setTimeout(() => {
          setShowRejectSpinner(false);
          setRefetchTrigger(!refetchTrigger);
        }, 1500);
      } else {
        setShowRejectSpinner(false);
        toast.error(response.data.message || "Failed to reject voice");
      }
    } catch (error) {
      setShowRejectSpinner(false);
      toast.error(error.response?.data?.message || "Failed to reject voice");
    }
  };

  const handleModalConfirm = () => {
    if (modal.action === "approve") {
      handleApprove(modal.adId);
      setModal({ open: false, action: null, adId: null });
    } else if (modal.action === "reject") {
      if (!rejectReason || rejectReason.trim() === "") {
        toast.error("❌ Please provide a reason for rejection!");
        return;
      }
      handleReject(modal.adId, rejectReason);
      setModal({ open: false, action: null, adId: null });
      setRejectReason("");
    }
  };

  const filteredData = voices.filter((voice) => {
    if (!searchTerm.trim()) return true;
    const s = searchTerm.toLowerCase();
    return (
      (voice._id || "").toLowerCase().includes(s) ||
      (voice.state || "").toLowerCase().includes(s) ||
      (voice.city || "").toLowerCase().includes(s) ||
      (voice.description || "").toLowerCase().includes(s)
    );
  });

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentPageData = filteredData.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  if (loading) return (
    <div className="min-h-[400px] flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-indigo-50 py-2 sm:py-6 px-2 sm:px-6">
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />

      {/* Compact Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-7xl mx-auto mb-4"
      >
        <div className="relative overflow-hidden bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-800 rounded-2xl shadow-xl p-4 sm:p-5 text-white">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -mr-10 -mt-10"></div>

          <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-white/20 backdrop-blur-md rounded-xl shadow-inner shrink-0">
                <FaBullhorn className="text-xl text-blue-200" />
              </div>
              <div>
                <h1 className="text-lg sm:text-xl font-black tracking-tight leading-none uppercase italic">
                  Available Assignments
                </h1>
                <p className="text-blue-100 text-[9px] sm:text-xs font-medium opacity-80 mt-1 uppercase tracking-tight">
                  Browse and accept new voice leads from the community.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-4 flex-1 max-w-md">
              <div className="relative flex-1 group">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50 group-focus-within:text-white transition-colors" />
                <input
                  type="text"
                  placeholder="Search leads..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white/10 backdrop-blur-md border border-white/20 rounded-xl py-2 pl-10 pr-4 text-xs font-bold placeholder:text-white/50 outline-none focus:ring-2 focus:ring-white/30 transition-all"
                />
              </div>
              <div className="bg-white/10 backdrop-blur-md px-3 py-1.5 rounded-xl border border-white/20 flex flex-col items-center min-w-[60px]">
                <div className="text-lg font-black leading-none">{filteredData.length}</div>
                <div className="text-[7px] font-black uppercase tracking-tighter opacity-70">Leads</div>
              </div>
              <button
                onClick={() => setRefetchTrigger(!refetchTrigger)}
                className="bg-white text-blue-700 hover:bg-blue-50 h-10 w-10 sm:w-auto sm:px-4 rounded-xl font-black uppercase text-[10px] tracking-wider transition-all shadow-lg flex items-center justify-center gap-2 group shrink-0"
              >
                <FaHistory className={`group-hover:rotate-180 transition-transform duration-500 ${loading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      <InfluenerRaiseVoiceTabs />

      <div className="max-w-7xl mx-auto">
        <AnimatePresence mode="wait">
          {currentPageData.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-[2rem] border border-slate-100 p-12 text-center"
            >
              <div className="bg-slate-50 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6">
                <FaSearch className="text-slate-200 text-4xl" />
              </div>
              <h3 className="text-xl font-black text-slate-800 mb-2">No Voice Requests Found</h3>
              <p className="text-slate-500 max-w-xs mx-auto text-sm uppercase font-bold tracking-tight">We couldn't find any complaints matching your context. Try again later.</p>
            </motion.div>
          ) : (
            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-12"
            >
              {currentPageData.map((voice, idx) => (
                <motion.div
                  key={voice._id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="bg-white rounded-2xl border border-slate-100 shadow-md hover:shadow-xl transition-all duration-300 flex overflow-hidden h-36 sm:h-40 group border-l-4 border-l-blue-500"
                >
                  <div className="relative w-28 sm:w-44 h-full bg-slate-900 shrink-0 overflow-hidden">
                    {voice.image ? (
                      <img
                        src={voice.image.startsWith("http") ? voice.image : `${process.env.REACT_APP_API_BASE_URL}/${voice.image.replace(/\\/g, "/")}`}
                        alt="voice-media"
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    ) : voice.video ? (
                      <video
                        src={voice.video.startsWith("http") ? voice.video : `${process.env.REACT_APP_API_BASE_URL}/${voice.video.replace(/\\/g, "/")}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-slate-100">
                        <FaBullhorn className="text-slate-300 text-2xl" />
                      </div>
                    )}
                    <div className="absolute top-2 left-2">
                      <span className={`px-2 py-0.5 rounded-md text-[8px] font-black uppercase text-white shadow-lg ${voice.status === 'modified' ? 'bg-indigo-600' : 'bg-amber-500'}`}>
                        {voice.status === 'modified' ? 'Modified' : 'New Post'}
                      </span>
                    </div>
                  </div>

                  <div className="p-4 flex-1 flex flex-col min-w-0">
                    <div className="flex justify-between items-start gap-2">
                      <h3 className="text-slate-900 font-extrabold text-sm sm:text-base leading-tight line-clamp-1 group-hover:text-blue-600 transition-colors uppercase">
                        {voice.description || 'Verified Intelligence'}
                      </h3>
                    </div>
                    <div className="flex items-center gap-2 text-[9px] font-bold text-slate-400 uppercase mt-1">
                      <FaMapMarkerAlt className="text-blue-500 shrink-0" />
                      <span className="truncate">{voice.city}, {voice.state}</span>
                      <span className="w-0.5 h-0.5 rounded-full bg-slate-300 shrink-0"></span>
                      <span>{new Date(voice.createdAt).toLocaleDateString('en-GB')}</span>
                    </div>
                    <p className="text-slate-500 text-[10px] leading-relaxed mt-2 line-clamp-2 italic opacity-80 min-h-[2.4em]">
                      "{voice.description || 'Detailed intelligence brief available in dossier...'}"
                    </p>
                    <div className="mt-auto flex items-center gap-2">
                      <button
                        onClick={() => setselectedAd(voice)}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all font-black text-[9px] uppercase tracking-wider"
                      >
                        <FaEye size={10} /> View
                      </button>
                      <button
                        onClick={() => setModal({ open: true, action: "approve", adId: voice._id })}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white transition-all font-black text-[9px] uppercase tracking-wider"
                      >
                        <FaCheck size={10} /> Accept
                      </button>
                      <button
                        onClick={() => setModal({ open: true, action: "reject", adId: voice._id })}
                        className="p-2 rounded-xl bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white transition-all"
                      >
                        <FaTimes size={10} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {totalPages > 1 && (
          <div className="flex justify-center mt-4 mb-20">
            <div className="flex items-center gap-2 bg-white p-1.5 rounded-2xl border border-slate-100 shadow-sm">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`w-10 h-10 flex items-center justify-center rounded-xl transition-all ${currentPage === 1 ? 'text-slate-200' : 'text-indigo-600 hover:bg-indigo-50 active:scale-90'}`}
              >
                ←
              </button>
              <div className="flex items-center px-4 gap-1">
                <span className="text-[10px] font-black text-slate-800 uppercase tracking-widest">Page</span>
                <span className="w-8 h-8 flex items-center justify-center bg-indigo-600 text-white rounded-lg text-xs font-black">{currentPage}</span>
                <span className="text-[10px] font-black text-slate-800 uppercase tracking-widest px-1">of</span>
                <span className="text-[10px] font-black text-slate-800 uppercase tracking-widest">{totalPages}</span>
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`w-10 h-10 flex items-center justify-center rounded-xl transition-all ${currentPage === totalPages ? 'text-slate-200' : 'text-indigo-600 hover:bg-indigo-50 active:scale-90'}`}
              >
                →
              </button>
            </div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {selectedAd && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center bg-slate-900/60 backdrop-blur-md z-50 p-4 sm:p-6"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col relative border border-slate-100"
            >
              <div className="p-6 border-b border-slate-50 flex items-center justify-between sticky top-0 bg-white/80 backdrop-blur-xl z-10">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-blue-200">
                    <FaInfoCircle size={24} />
                  </div>
                  <div>
                    <h2 className="text-lg font-black text-slate-800 uppercase tracking-tight">Voice Dossier</h2>
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">ID: {selectedAd._id.slice(-8)}</div>
                  </div>
                </div>
                <button
                  onClick={() => setselectedAd(null)}
                  className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 hover:bg-rose-50 hover:text-rose-500 transition-all font-bold"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                <div className="rounded-3xl overflow-hidden bg-slate-900 aspect-video relative border-4 border-slate-50 shadow-inner">
                  {selectedAd.image ? (
                    <img
                      src={selectedAd.image.startsWith("http") ? selectedAd.image : `${process.env.REACT_APP_API_BASE_URL}/${selectedAd.image.replace(/\\/g, "/")}`}
                      alt="enlarged-media"
                      className="w-full h-full object-cover"
                    />
                  ) : selectedAd.video ? (
                    <video
                      src={selectedAd.video.startsWith("http") ? selectedAd.video : `${process.env.REACT_APP_API_BASE_URL}/${selectedAd.video.replace(/\\/g, "/")}`}
                      className="w-full h-full object-cover"
                      controls
                    />
                  ) : null}
                </div>

                <div className="space-y-6">
                  <section>
                    <h4 className="text-[10px] font-black text-blue-500 uppercase tracking-[0.2em] mb-2 px-1">Briefing</h4>
                    <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100 text-slate-700 font-bold italic leading-relaxed text-sm">
                      "{selectedAd.description || "No detailed briefing available for this post."}"
                    </div>
                  </section>
                  <div className="grid grid-cols-2 gap-4">
                    <section className="bg-white rounded-2xl border border-slate-100 p-4">
                      <h4 className="text-[10px] font-black text-indigo-500 uppercase tracking-[0.2em] mb-2">Location</h4>
                      <div className="flex items-center gap-2 text-xs font-bold text-slate-700">
                        <FaMapMarkerAlt className="text-rose-500 shrink-0" />
                        <span className="truncate">{selectedAd.city}, {selectedAd.state}</span>
                      </div>
                    </section>
                    <section className="bg-white rounded-2xl border border-slate-100 p-4">
                      <h4 className="text-[10px] font-black text-emerald-500 uppercase tracking-[0.2em] mb-2">Posted</h4>
                      <div className="flex items-center gap-2 text-xs font-bold text-slate-700">
                        <FaCalendarAlt className="text-emerald-500 shrink-0" />
                        <span>{new Date(selectedAd.createdAt).toLocaleDateString()}</span>
                      </div>
                    </section>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-slate-50 border-t border-slate-100 flex gap-3">
                <button
                  onClick={() => { setModal({ open: true, action: "approve", adId: selectedAd._id }); setselectedAd(null); }}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-xl font-black uppercase tracking-widest text-[10px] shadow-lg transition-all"
                >
                  Accept Assignment
                </button>
                <button
                  onClick={() => { setModal({ open: true, action: "reject", adId: selectedAd._id }); setselectedAd(null); }}
                  className="bg-white text-rose-600 border border-rose-100 hover:bg-rose-50 px-6 py-3 rounded-xl font-black uppercase tracking-widest text-[10px] transition-all"
                >
                  Decline
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {modal.open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm z-[60] p-4"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-[2rem] shadow-2xl p-8 w-full max-w-sm text-center border border-slate-100"
            >
              <div className={`mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-lg ${modal.action === 'approve' ? 'bg-emerald-100 text-emerald-600 shadow-emerald-100' : 'bg-rose-100 text-rose-600 shadow-rose-100'}`}>
                {modal.action === 'approve' ? <FaCheck size={32} /> : <FaTimes size={32} />}
              </div>
              <h2 className="text-xl font-black text-slate-800 mb-2 uppercase tracking-tight">
                {modal.action === 'approve' ? 'Confirm Acceptance' : 'Decline Voice'}
              </h2>
              <p className="text-slate-500 text-[11px] mb-8 font-black uppercase tracking-widest opacity-60">
                {modal.action === 'approve' ? 'Are you sure about this task?' : 'Submit reason for withdrawal.'}
              </p>
              {modal.action === 'reject' && (
                <div className="mb-8">
                  <textarea
                    placeholder="Brief reason for withdrawal..."
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-100 rounded-2xl p-4 text-xs outline-none resize-none font-bold text-slate-700 placeholder:text-slate-300"
                    rows={4}
                  />
                </div>
              )}
              <div className="space-y-2">
                <button
                  onClick={handleModalConfirm}
                  className={`w-full py-4 rounded-2xl font-black uppercase tracking-widest text-[10px] transition-all ${modal.action === 'approve' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200' : 'bg-rose-600 text-white shadow-lg shadow-rose-200'}`}
                >
                  Confirm Action
                </button>
                <button
                  onClick={() => setModal({ open: false, action: null, adId: null })}
                  className="w-full py-4 rounded-2xl font-black uppercase tracking-widest text-[10px] text-slate-400 hover:text-slate-600"
                >
                  Go Back
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {(showApproveSpinner || showRejectSpinner) && (
        <div className="fixed inset-0 flex items-center justify-center bg-white/90 backdrop-blur-xl z-[100]">
          <div className="flex flex-col items-center">
            <div className={`w-16 h-16 border-4 rounded-full border-t-transparent animate-spin ${showApproveSpinner ? 'border-emerald-600' : 'border-rose-600'}`}></div>
            <p className="mt-4 text-[10px] font-black uppercase tracking-widest text-slate-800">Processing Sequence...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default InfluenerGetNewRaiseVoice;
